import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_ENVIRONMENT, AceNotificationService, AppCookieService, DataService } from '@ace/ace';
import { PlatformFeaturesService } from '../../services/platform-features.service';

@Injectable()
export class HomeworkAwareDataService extends DataService
{
  constructor(
    @Inject(APP_ENVIRONMENT)
    environment: any,
    toastr: AceNotificationService,
    http: HttpClient,
    appCookieService: AppCookieService,
    private platformFeaturesService: PlatformFeaturesService
  )
  {
    super(environment, toastr, http, appCookieService);
  }

  override async getData(url: any, params?: any, headers?: any, menuName?: string): Promise<any>
  {
    const data = await super.getData(url, params, headers, menuName);

    if (!this.shouldHandleMenus(url))
    {
      return data;
    }

    const homeworkFiltered = this.shouldFilterHomework()
      ? this.filterHomeworkMenus(data)
      : data;

    return this.filterPlatformFeatureMenus(homeworkFiltered);
  }

  private shouldHandleMenus(url: any): boolean
  {
    return String(url ?? '').includes('/ace-common/api/menus')
      && !String(url ?? '').includes('/portal-order');
  }

  private shouldFilterHomework(): boolean
  {
    return (this as any).environment?.HOMEWORK_HIDDEN === true;
  }

  private filterHomeworkMenus(items: any): any
  {
    if (!Array.isArray(items))
    {
      return items;
    }

    return items
      .filter(item => !this.isHomeworkMenu(item))
      .map(item => ({
        ...item,
        children: this.filterHomeworkMenus(item.children)
      }));
  }

  private isHomeworkMenu(item: any): boolean
  {
    const pageName = String(item?.pageName ?? '').trim().toLowerCase();
    const ref = String(item?.ref ?? '').trim().toLowerCase();
    const route = String(item?.route ?? '').trim().toLowerCase();

    return pageName === 'homework'
      || ref === 'homework'
      || route === '/main/homework'
      || route === '/homework'
      || route === 'homework';
  }

  private filterPlatformFeatureMenus(items: any): any
  {
    if (!Array.isArray(items))
    {
      return items;
    }

    return items
      .filter(item => this.isPlatformMenuEnabled(item))
      .map(item => ({
        ...item,
        children: this.filterPlatformFeatureMenus(item.children)
      }));
  }

  private isPlatformMenuEnabled(item: any): boolean
  {
    const keys = [item?.ref, item?.route, item?.pageName]
      .map(value => this.normalizeMenuKey(String(value ?? '')))
      .filter(Boolean);

    if (keys.some(key => this.isAssessmentMenuKey(key)))
    {
      return this.platformFeaturesService.isEnabled('assessment');
    }
    if (keys.some(key => key === 'game-content' || key.endsWith('/game-content')))
    {
      return this.platformFeaturesService.isEnabled('gamification');
    }
    if (keys.some(key => key === 'copilot-studio' || key.endsWith('/copilot-studio')))
    {
      return this.platformFeaturesService.isEnabled('aiAuthoring');
    }
    return true;
  }

  private isAssessmentMenuKey(key: string): boolean
  {
    return [
      'quizzes',
      'exams',
      'homework',
      'question-bank',
      'question-library',
      'assessment',
      'assessment-marking',
      'assessment/marking',
      'quiz-corrections',
      'exam-corrections',
      'homework-corrections',
    ].includes(key)
      || key.endsWith('/assessment/marking');
  }

  private normalizeMenuKey(value: string): string
  {
    return value
      .trim()
      .toLowerCase()
      .replace(/^\/?main\/?/, '')
      .replace(/[_\s]+/g, '-');
  }
}
